import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { gql, useMutation } from '@apollo/client';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { toast } from 'sonner';

const RESPOND_TO_INSPECTION = gql`
   mutation RespondToInspection(
      $inspectionId: ID!
      $response: InspectionResponse!
      $reason: String
      $preferredSchedule: PreferredScheduleInput
   ) {
      respondToInspectionSchedule(
         inspectionId: $inspectionId
         response: $response
         reason: $reason
         preferredSchedule: $preferredSchedule
      ) {
         id
         inspectionStatus
      }
   }
`;

const InspectionResponseModal = ({ isOpen, onClose, inspection }) => {
   const [response, setResponse] = useState('ACCEPT');
   const [reason, setReason] = useState('');
   const [preferredDate, setPreferredDate] = useState(null);
   const [preferredTime, setPreferredTime] = useState('');

   const [respondToInspection, { loading }] = useMutation(RESPOND_TO_INSPECTION, {
      onCompleted: () => {
         const message = response === 'ACCEPT'
            ? 'Inspection schedule accepted'
            : 'Reschedule request sent';
         toast.success(message);
         onClose();
      },
      onError: (error) => {
         console.error('Error submitting response:', error);
         toast.error(error.message);
      },
      refetchQueries: ['GetUserInspections'] // This will refresh the data
   });

   const handleDateChange = (date) => {
      setPreferredDate(date);
      if (date) {
         setPreferredTime(format(date, 'HH:mm'));
      }
   };

   const handleTimeChange = (time) => {
      setPreferredTime(time);
   };

   const handleSubmit = async () => {
      if (!inspection?.id) {
         toast.error('Invalid inspection data');
         return;
      }

      try {
         if (response === 'REQUEST_RESCHEDULE') {
            if (!preferredDate) {
               toast.error('Please select a date for rescheduling');
               return;
            }

            const timeFromDate = preferredDate ? format(preferredDate, 'HH:mm') : '';
            const finalTime = preferredTime || timeFromDate;

            await respondToInspection({
               variables: {
                  inspectionId: inspection.id,
                  response,
                  reason: reason.trim(),
                  preferredSchedule: {
                     date: format(preferredDate, 'yyyy-MM-dd'),
                     time: finalTime
                  }
               }
            });
         } else {
            await respondToInspection({
               variables: {
                  inspectionId: inspection.id,
                  response,
                  reason: reason.trim()
               }
            });
         }
      } catch (error) {
         console.error('Error submitting response:', error);
         toast.error(error.message);
      }
   };

   return (
      <Dialog open={isOpen} onOpenChange={onClose}>
         <DialogContent>
            <DialogHeader>
               <DialogTitle>Respond to Inspection Schedule</DialogTitle>
               <DialogDescription>
                  Accept the schedule or request for reschedule
               </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
               <div className="flex gap-2">
                  <Button
                     variant="outline"
                     onClick={() => setResponse('ACCEPT')}
                     className={response === 'ACCEPT' ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white' : ''}
                  >
                     Accept
                  </Button>
                  <Button
                     variant="outline"
                     onClick={() => setResponse('REQUEST_RESCHEDULE')}
                     className={response === 'REQUEST_RESCHEDULE' ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white' : ''}
                  >
                     Request Reschedule
                  </Button>
               </div>

               {response === 'REQUEST_RESCHEDULE' && (
                  <div className="space-y-4">
                     <div className="flex gap-1">
                        <input
                           type="date"
                           value={preferredDate ? format(preferredDate, "yyyy-MM-dd") : ""}
                           onChange={(e) => {
                              const date = e.target.value ? new Date(e.target.value) : null;
                              handleDateChange(date);
                           }}
                           className="w-[150px] h-10 px-3 py-2 rounded-md border border-input bg-background text-sm"
                        />
                        <input
                           type="time"
                           value={preferredTime}
                           onChange={(e) => handleTimeChange(e.target.value)}
                           className="w-[150px] h-10 px-3 py-2 rounded-md border border-input bg-background text-sm"
                        />
                     </div>
                  </div>
               )}

               <Textarea
                  placeholder={response === 'ACCEPT' ? 'Additional notes (optional)' : 'Reason for reschedule'}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required={response === 'REQUEST_RESCHEDULE'}
               />

               <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={onClose}>
                     Cancel
                  </Button>
                  <Button
                     onClick={handleSubmit}
                     disabled={loading}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     Submit Response
                  </Button>
               </div>
            </div>
         </DialogContent>
      </Dialog>
   );
};

export default InspectionResponseModal;
